import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import { patientService } from '@/services/patientService';
import { useContent } from '@/hooks/useContent';

/**
 * Compact "Create an account" card shown in the sidebar in place of the sign-in form
 * (NavAccountLogin) to guest-access patients who have no portal account — e.g. after exiting an
 * iCode order/reschedule workflow. Get Started seeds the BFF's account-creation HTTP session for
 * the guest's patient (identity resolved server-side from the session) and opens the
 * account-creation wizard directly on the password step. If seeding fails (or the session has
 * since expired), CreatePasswordStep's own timeout handling routes the user back to re-verify.
 *
 * The guest session is intentionally left intact until account creation completes: navigating to
 * the public /create-account route is enough, and tearing down auth first would let ProtectedRoute
 * (still mounted on /appointments) bounce the user to /sign-in before the navigation commits.
 */
export default function NavCreateAccount() {
  const navigate = useNavigate();
  const { getContent } = useContent();
  const [loading, setLoading] = useState(false);

  const handleGetStarted = async () => {
    setLoading(true);
    try {
      await patientService.issueCreateAccountToken();
    } catch {
      // Ignore — CreatePasswordStep falls back to the verify step if the session wasn't seeded.
    } finally {
      setLoading(false);
    }
    navigate('/create-account', { state: { skipTo: 'createPassword' } });
  };

  return (
    <Box sx={{ px: 0.5, mt: 0.5, mb: 2 }}>
      <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', mb: 0.75 }}>
        <PersonAddAltOutlinedIcon sx={{ fontSize: 16, color: 'primary.main' }} />
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.6875rem' }}
        >
          {getContent('PPApptBookedCreateAccountTitle')}
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, lineHeight: 1.4 }}>
        {getContent('PPApptBookedCreateAccountBody')}
      </Typography>
      <Button
        onClick={() => { void handleGetStarted(); }}
        variant="contained"
        size="small"
        fullWidth
        disabled={loading}
        startIcon={loading ? <CircularProgress size={12} color="inherit" /> : undefined}
        sx={{ borderRadius: 2, fontSize: '0.75rem', textTransform: 'none', fontWeight: 600, py: 0.6 }}
      >
        {getContent('PPApptBookedGetStarted')}
      </Button>
    </Box>
  );
}
